// @flow
import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import Select from 'react-select';
import { get, set, flow, mapValues, isUndefined, map, sortBy, uniqBy } from 'lodash/fp';

import FlexiSelect from 'app/components/commons/FlexiSelect';
import ButtonToggle from 'app/components/commons/ButtonToggle';
import Divider from 'app/components/commons/Divider';
import Icon from 'app/components/commons/Icon';
import InputField from 'app/components/commons/InputField';
import { updateEntityProperty } from 'app/actions/challengeActions';
import { updateSettings } from 'app/actions/fantasyInsiderActions';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';
import Clickable from 'app/components/commons/Clickable';
import entitiesSelector from 'app/selectors/entitiesSelector';

import ExposureTable from '../ExposureTable';

type Props = {
  competitionName?: string,
  settings: Object,
  theme: Object,
  sportHasTeam?: boolean,
  activeSettingsTab: string,
  onSelectSettingsTab: (tab: string) => void,
  entities: Array<Object>,
  onUpdateEntityProperty: (idStr: string, field: string, value: any) => void,
  onUpdateSettings: (data: Object) => void
};

const defaultProps = {
  sportHasTeam: true,
  competitionName: undefined
};

const IconToggleOn = styled(props => <Icon type="icon-toggle-on" {...props} />)(({ theme }) => ({
  color: theme.color.success
}));

const IconToggleOff = styled(props => <Icon type="icon-toggle-on" {...props} />)(({ theme }) => ({
  transform: 'rotate(180deg)',
  color: theme.color.disabled
}));

const switches = [
  {
    field: 'avoidQuestionable',
    label: 'Avoid Questionable & Doubtful Players'
  },
  {
    field: 'avoidWarning',
    label: 'Avoid players with warning icons'
  },
  {
    field: 'ignoreProjections',
    label: 'Ignore projections (treat all players equally)'
  },
  {
    field: 'avoidLow100s',
    label: 'Avoid players without a score over 100 (AFL Only)'
  }
];

const switchesWithoutTeam = [
  {
    field: 'ignoreProjections',
    label: 'Ignore projections (treat all players equally)'
  }
];

const portfolioMethods = [
  {
    value: 'unique',
    label: 'Unique Players and Max Exposure'
  },
  {
    value: 'degrade1',
    label: 'Reduce included players by 1%'
  },
  {
    value: 'degrade2_5',
    label: 'Reduce included players by 2.5%'
  },
  {
    value: 'degrade5',
    label: 'Reduce included players by 5%'
  },
  {
    value: 'degrade10',
    label: 'Reduce included players by 10%'
  },
  {
    value: 'random',
    label: 'Select random scenario for each lineup'
  }
];

const INPUT_HEIGHT = 34;

const SettingsContent = ({
  competitionName,
  onUpdateEntityProperty,
  onUpdateSettings,
  onSelectSettingsTab,
  activeSettingsTab,
  sportHasTeam,
  entities,
  theme,
  settings
}: Props) => {
  const {
    numLineUps,
    maxExposurePerPlayer,
    numPlayersForTeamStacking,
    portfolioMethod,
    uniquePlayers,
    salaryCap,
    maxPlayersPerTeam,
    minSalary,
    minCeiling,
    teamStacking
  } = settings;

  const teamOptions = flow(
    uniqBy('insiderTeam'),
    sortBy('insiderTeam'),
    map(({ insiderTeam }) => ({ value: insiderTeam, label: insiderTeam }))
  )(entities);

  const inputsOptions = {
    numLineUps: {
      label: 'Number of Lineups',
      defaultValue: numLineUps
    },
    uniquePlayers: {
      label: 'Unique Players Per Lineup',
      defaultValue: uniquePlayers
    },
    minSalary: {
      label: 'Min Salary Cap',
      defaultValue: minSalary
    },
    salaryCap: {
      label: 'Max Salary Cap',
      defaultValue: salaryCap
    },
    minCeiling: {
      label: 'Exclude players with a ceiling below',
      defaultValue: minCeiling
    }
  };

  if (sportHasTeam) {
    inputsOptions.maxPlayersPerTeam = {
      label: 'Max Players per team / race',
      defaultValue: maxPlayersPerTeam
    };
  }

  const switchOptions = sportHasTeam ? switches : switchesWithoutTeam;

  const [inputValues, setInputValues] = useState(
    Object.assign({}, mapValues('defaultValue', inputsOptions), {
      maxExposurePerPlayer,
      numPlayersForTeamStacking,
      teamStacking,
      portfolioMethod
    })
  );

  const handleUpdateSetting = (field: string, { isInteger, value } = {}) => {
    const val = value || get(field, inputValues);

    if (!isUndefined(val)) {
      const newVal = isInteger ? parseInt(val, 10) : val;
      if (newVal !== get(field, settings)) {
        onUpdateSettings({ [field]: newVal });
      }
    }
  };

  const handlePortfolioChange = value => {
    setInputValues(set(['portfolioMethod'], value, inputValues));
    handleUpdateSetting('portfolioMethod', { value });
  };

  return (
    <div>
      <div className="columns is-centered">
        <div className="column is-4">
          <ButtonToggle
            size="small"
            onChange={onSelectSettingsTab}
            options={[
              {
                label: 'Basic',
                value: 'basic'
              },
              {
                label: 'Exposure',
                value: 'exposure'
              }
            ]}
            value={activeSettingsTab}
          />
        </div>
      </div>
      {activeSettingsTab === 'basic' && (
        <div>
          <h3>General</h3>
          <Columns>
            <Column className="is-half">
              {Object.entries(inputsOptions).map(([field, { label }]) => (
                <Columns className="is-vcentered" key={field}>
                  <Column className="is-half">
                    <span>{label}</span>
                  </Column>
                  <Column className="is-half">
                    <InputField
                      noMargin
                      autoBorderRadius={false}
                      type="number"
                      name={field}
                      value={get(field, inputValues)}
                      size="small"
                      onBlur={() => handleUpdateSetting(field, { isInteger: true })}
                      onChange={val => setInputValues(set([field], val, inputValues))}
                      lightContent
                      inputStyle={{ width: 40, height: INPUT_HEIGHT }}
                    />
                  </Column>
                </Columns>
              ))}
            </Column>
            <Column className="is-narrow">
              <Divider vertical />
            </Column>
            <Column className="is-half">
              {switchOptions.map(({ field, label }) => (
                <Columns className="is-vcentered" key={field}>
                  <Column className="is-1">
                    <Clickable
                      onClick={() => onUpdateSettings({ [field]: !get(field, settings) })}
                      style={{ justifyContent: 'flex-start' }}
                    >
                      {get(field, settings) ? (
                        <IconToggleOn size={40} />
                      ) : (
                        <IconToggleOff size={40} />
                      )}
                    </Clickable>
                  </Column>
                  <Column className="is-half">
                    <span>{label}</span>
                  </Column>
                </Columns>
              ))}
            </Column>
          </Columns>
        </div>
      )}
      {activeSettingsTab === 'exposure' && (
        <Columns>
          <Column>
            <h3>Global Exposure</h3>
            <Columns className="is-vcentered">
              <Column className="is-half">
                <div>Set maxmium exposure per player</div>
              </Column>
              <Column className="is-half">
                <InputField
                  noMargin
                  autoBorderRadius={false}
                  type="number"
                  value={get('maxExposurePerPlayer', inputValues)}
                  size="small"
                  onBlur={() => handleUpdateSetting('maxExposurePerPlayer', { isInteger: true })}
                  onChange={val => setInputValues(set(['maxExposurePerPlayer'], val, inputValues))}
                  lightContent
                  inputStyle={{ width: 40, height: INPUT_HEIGHT }}
                />
              </Column>
            </Columns>
            {sportHasTeam && (
              <React.Fragment>
                <h3>Team Stacking</h3>
                <Columns className="is-vcentered">
                  <Column>
                    <div>Teams</div>
                  </Column>
                  <Column>
                    <Select
                      isMulti={competitionName !== 'NFL'}
                      styles={{
                        container: provided => ({
                          ...provided,
                          height: 31
                        }),
                        control: provided => ({
                          ...provided,
                          background: theme.color.basis6,
                          border: 0,
                          borderRadius: 0
                        }),
                        menu: provided => ({
                          ...provided,
                          background: theme.color.basis6
                        }),
                        option: (provided, { isFocused }) => ({
                          ...provided,
                          color: isFocused ? theme.color.white : theme.color.black,
                          background: isFocused ? theme.color.primary : theme.color.basis6
                        })
                      }}
                      name="team"
                      options={teamOptions}
                      value={get('teamStacking', inputValues)}
                      defaultText="Select team"
                      lightContent
                      onBlur={() => handleUpdateSetting('teamStacking')}
                      onChange={val =>
                        setInputValues(
                          set(['teamStacking'], Array.isArray(val) ? val : [val], inputValues)
                        )
                      }
                    />
                  </Column>
                </Columns>
              </React.Fragment>
            )}
            {sportHasTeam && (
              <Columns className="is-vcentered">
                <Column>
                  <span>Number of Players</span>
                </Column>
                <Column>
                  <InputField
                    noMargin
                    autoBorderRadius={false}
                    type="number"
                    value={get('numPlayersForTeamStacking', inputValues)}
                    size="small"
                    onBlur={() =>
                      handleUpdateSetting('numPlayersForTeamStacking', { isInteger: true })
                    }
                    onChange={val =>
                      setInputValues(set(['numPlayersForTeamStacking'], val, inputValues))
                    }
                    lightContent
                    inputStyle={{ width: 40, height: INPUT_HEIGHT }}
                  />
                </Column>
              </Columns>
            )}
            <h3>Portfolio Method</h3>
            <Columns className="is-vcentered">
              <Column>
                <span>Choose Method</span>
              </Column>
              <Column>
                <FlexiSelect
                  loading={false}
                  onChange={handlePortfolioChange}
                  options={portfolioMethods}
                  placeholder="Select method"
                  value={get('portfolioMethod', inputValues)}
                  height={INPUT_HEIGHT}
                />
              </Column>
            </Columns>
          </Column>
          <Column className="is-narrow">
            <Divider vertical />
          </Column>
          <Column>
            <h3>Exposure Management</h3>
            <ExposureTable
              entities={sortBy(({ isFavorite }) => !isFavorite, entities)}
              defaultExposure={maxExposurePerPlayer}
              onUpdateExposure={onUpdateEntityProperty}
            />
          </Column>
        </Columns>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const entities = entitiesSelector(state, { isCruncher: true });

  return {
    competitionName: get('challenge.competitionName', state),
    settings: state.fantasyInsider.settings,
    entities: entities.filter(p => !!p.insiderId),
    salaryCap: get('challenge.salaryCap', state),
    maxPlayersPerTeam: get('challenge.playersPerSideCap', state) || 0
  };
};

const actions = {
  onUpdateSettings: updateSettings,
  onUpdateEntityProperty: updateEntityProperty
};

SettingsContent.defaultProps = defaultProps;

export default withTheme(connect(mapStateToProps, actions)(SettingsContent));
